import {
    Box,
    Button,
    CircularProgress,
    IconButton,
    Menu,
    MenuItem,
    Tooltip,
    Grid,
    Typography,
} from '@mui/material';
import { profileObjAtom, userInformationAtom } from '../../../Services/_states/index.js';
import { ProfileObject } from '../../../interfaces/Management.interface.ts';
import { MaterialReactTable, MRT_ColumnDef } from 'material-react-table';
import React, { useEffect, useMemo, useState } from 'react';
import { ManagementActions } from '../../../Services/_actions/Management/management.actions.ts';
import { PaginatedResponse, FilterObj, RoleFormData, UserObject } from '../../../interfaces/Management.interface.ts';
import SearchBox from '../../FlowBuilder/Common/SearchBox/index.tsx';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import Pagination from '../../FlowBuilder/Common/Pagination/index.tsx';
import { useNavigate } from 'react-router';
import { useRecoilState, useRecoilValue, useResetRecoilState, useSetRecoilState } from 'recoil';
import { createUserFormDataAtom } from '../../../Services/_states/index.js';
import ChangePasswordModal from './ChangePassword.tsx';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ConfirmBox from '../../FlowBuilder/Common/ConfirmBox/index.tsx';
import { useSnackBar } from '../../FlowBuilder/Common/SnackBar/index.tsx';
import OrganisationModal from './OrganisationDetailModal.tsx';
import DynamicBreadcrumbs from '../../Shared/Breadcrumb/index.tsx';
import OrganisationDropdown from '../../Shared/OrganisationDropdown/index.tsx';
import HomeIcon from '@mui/icons-material/Home';
import useDebounce from '../../Shared/Delay/index.tsx';
import CommonTable from '../../Shared/Tables/TableV1.tsx';

const List: React.FC = () => {
    const actions = ManagementActions();
    const loggedInUser = useRecoilValue(userInformationAtom);
    const navigate = useNavigate();
    const { showSnackBar } = useSnackBar();
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const [userToDelete, setUserToDelete] = useState < UserObject | null > (null);
    const [indexToRemove, setIndexToRemove] = useState < number | null > (null);
    const [createUserFormData, setCreateUserFormData] = useRecoilState < UserObject > (createUserFormDataAtom);
    const resetCreateUserFormData = useResetRecoilState(createUserFormDataAtom);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [filtersObj, setFiltersObj] = useState < FilterObj > ({
        limit: 20,
        search: '',
        page: 1,
        sortOrder: 'desc',
        sortField: 'created_at',
        selectedOrganisation: null,
        organisation_id: "",
    });
    const setProfileObject = useSetRecoilState(profileObjAtom);
    const [userResponse, setUserResponse] = useState < PaginatedResponse > ({ count: 0, list: [] });
    const [isLoading, setIsLoading] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedOrganisation, setSelectedOrganisation] = useState < any | null > (null);
    const columnVisibility = {
        'profile.organisation.name': loggedInUser?.is_superuser || false,
        'is_superuser': loggedInUser?.is_superuser || false,
    };

    const handleOpenModal = (organisation: any) => {
        setSelectedOrganisation(organisation);
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        setSelectedOrganisation(null);
    };

    const handleEdit = (data) => {
        if (!data) {
            showSnackBar("User details is missing", 'info');
            return
        }
        const { organisation, ...rest } = data?.profile;
        setCreateUserFormData({
            ...data,
            organisation: organisation ?? null,
            profile: rest ?? null,
        });
        navigate("/r/management/user/edit/");
    };

    const handleChangePassword = (data: UserObject) => {
        setCreateUserFormData(data);
        setIsModalOpen(true);
    }

    const handleEdits = (data: ProfileObject) => {
        setProfileObject(data);
        navigate('/r/management/profile-permissions/view');
    };
    const handleDeleteRequest = (data: UserObject, index: number) => {
        setUserToDelete(data);
        setIndexToRemove(index);
        setIsConfirmOpen(true);
    };

    const handleUnlockUser = async (data: UserObject) => {
  try {
    const res = await actions.unlockUser(data.username);
    if (res?.status === 200) {
      showSnackBar("User unlocked successfully!", "success");
    } else {
      showSnackBar("Failed to unlock user.", "error");
    }
  } catch (error) {
    showSnackBar("Error unlocking user.", "error");
  }
};


    const handleDelete = async (confirmed: boolean) => {
        if (confirmed && userToDelete && indexToRemove !== null) {
            try {
                const res = await actions.deleteUser(userToDelete?.id);
                if (res?.status === 200) {
                    setUserResponse((prevState) => {
                        const updatedList = prevState?.list?.filter((_, idx) => idx !== indexToRemove);
                        return { ...prevState, list: updatedList };
                    });
                    showSnackBar('User deleted successfully.', 'success');
                } else {
                    showSnackBar('Failed to delete user. Please try again.', 'error');
                }
            } catch (error) {
                showSnackBar('An error occurred while deleting the user:', 'error');
            }
        }
        setIsConfirmOpen(false);
        setUserToDelete(null);
        setIndexToRemove(null);
    };

    const columns = useMemo < MRT_ColumnDef < UserObject > [] > (
        () => [
            {
                accessorKey: 'index',
                header: 'S.No.',
                size: 50,
                Cell: ({ row }) => {
                    const rowIndex = row.index;
                    const page = (filtersObj?.page - 1) ?? 0;
                    const limit = filtersObj?.limit ?? 10;
                    return page * limit + rowIndex + 1;
                },
                enableSorting: false
            },
            {
                accessorKey: 'first_name',
                header: 'First Name',
                enableSorting: false,
                size: 100,
            },
            {
                accessorKey: 'last_name',
                header: 'Last Name',
                enableSorting: false,
                size: 100,
            },
            {
                accessorKey: 'is_superuser',
                header: 'Is SuperUser',
                Cell: ({ cell }) => (cell.getValue() ? 'True' : 'False'),
                enableSorting: false,
                size: 100,
            },
            {
                accessorKey: 'email',
                header: 'Email',
                enableSorting: false,
                size: 100,
            },
            {
                accessorKey: 'username',
                header: 'Username',
                enableSorting: false,
                size: 100,
            },
            {
                accessorKey: 'profile.organisation.name',
                header: 'Organisation',
                enableSorting: false,
                size: 80,
                Cell: ({ cell }: any) => (
                    <a
                        href="#"
                        onClick={(e) => {
                            e.preventDefault();
                            handleOpenModal(cell.getValue());
                        }}
                    >
                        {cell.getValue()}
                    </a>
                ),
            },
            {
                accessorKey: 'profile.name',
                header: 'Profile',
                enableSorting: false,
                size: 80,
                Cell: ({ cell, row }: any) => (
                    <a
                        href="#"
                        onClick={(e) => {
                            e.preventDefault();
                            handleEdits(row.original.profile);
                        }}
                    >
                        {cell?.getValue()}
                    </a>
                ),
            },
            {
                accessorKey: 'actions',
                enableSorting: false,
                header: 'Actions',
                size: 50,
                Cell: ({ row }) => {
                    const [anchorEl, setAnchorEl] = React.useState < null | HTMLElement > (null);
                    const open = Boolean(anchorEl);

                    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
                        setAnchorEl(event.currentTarget);
                    };

                    const handleClose = () => {
                        setAnchorEl(null);
                    };

                    return (

                        <div>
                            <IconButton onClick={handleClick}>
                                <MoreVertIcon />
                            </IconButton>
                            <Menu
                                id={`menu-${row.index}`}
                                anchorEl={anchorEl}
                                open={open}
                                onClose={handleClose}
                                MenuListProps={{
                                    'aria-labelledby': `menu-button-${row.index}`,
                                }}
                            >
                                <MenuItem
                                    onClick={() => {
                                        handleEdit(row.original);
                                        handleClose();
                                    }}
                                >
                                    Edit
                                </MenuItem>
                                <MenuItem
                                    onClick={() => {
                                        handleChangePassword(row.original);
                                        handleClose();
                                    }}
                                >
                                    Change Password
                                </MenuItem>
                                 <MenuItem
                                   onClick={() => {
                                      handleUnlockUser(row.original);
                                      handleClose();
                                    }}
                                  >
                                    Unlock User
                                  </MenuItem>

                                <MenuItem
                                    onClick={() => {
                                        handleDeleteRequest(row.original, row.index);
                                        handleClose();
                                    }}
                                >
                                    Delete
                                </MenuItem>
                            </Menu>
                        </div>
                    )
                },
            },
        ],
        [filtersObj]
    );

    const getUserList = async (filterObj: FilterObj) => {
        setIsLoading(true);
        try {
            const res = await actions.getUserList(filterObj);
            setUserResponse(res?.status === 200 ? res?.data || {} : {});
        } catch (error) {
            showSnackBar('Error fetching roles:', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const debouncedSearch = useDebounce(filtersObj.search, 500);
    useEffect(() => {
        if (debouncedSearch || debouncedSearch === '') {
            getUserList({ ...filtersObj, search: debouncedSearch, page: 1 });
        }
    }, [debouncedSearch]);

    const handleSearch = (e) => {
        setFiltersObj({ ...filtersObj, search: e, page: 1 });
        // getUserList({ ...filtersObj, search: e, page: 0 });
    }
    const handleChangePage = (event, newPage) => {
        getUserList({ ...filtersObj, page: newPage + 1 });
        setFiltersObj({ ...filtersObj, page: newPage + 1 });
    };
    const handleChangeRowsPerPage = (event) => {
        const value = parseInt(event.target.value) ?? 20;
        getUserList({
            ...filtersObj,
            limit: value,
            page: 1,
        });
        setFiltersObj({
            ...filtersObj,
            limit: value,
            page: 1,
        });
    };

    const handleSubmit = async (password: string) => {
        try {
            let res = await actions.changeUserPassword({ password: password, id: createUserFormData?.id });
            if (res.status === 200) {
                showSnackBar(res?.data?.data, "success");
            } else {
                showSnackBar('Password must not match the last 8 used passwords.', 'error');
            }
        } catch (error) {
            showSnackBar(error?.toString(), 'error');
        }
        resetCreateUserFormData();
    }

    const breadcrumbsData = [
        { label: <IconButton color='primary'><HomeIcon /></IconButton>, href: "/" },
        { label: 'Users' },
    ];

    // useEffect(() => {
    //     getUserList(filtersObj);
    // }, []);

    return (
        <Box sx={{ padding: '5px', backgroundColor: '#f8f9fa', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)' }}>
            <DynamicBreadcrumbs items={breadcrumbsData} onBreadcrumbClick={(event, item: { label: string, href: string }) => {
                if (item) {
                    navigate(item?.href);
                }
            }}
            />
            <Grid container spacing={2} sx={{ alignItems: 'center' }}>
                <Grid item xs={6} sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
                        User Management
                    </Typography>
                </Grid>
                <Grid item xs={6} textAlign="right">
                    <Button variant="contained" color="primary" size="small" onClick={() => { navigate('/r/management/user/create'); resetCreateUserFormData(); }}>Add</Button>
                </Grid>
            </Grid>
            {/* <Grid container sx={{ alignItems: 'center' }} className='search-box-style'>
                <Grid item xs={12} md={9} sx={{ display: 'flex', alignItems: 'center' }}>
                    <SearchBox
                        handleSearch={handleSearch}
                        value={filtersObj?.search || ''}
                    />
                </Grid>
                {loggedInUser?.is_superuser &&
                    <Grid item xs={12} md={3} textAlign="right" sx={{ pb: 0.7 }}>
                        <OrganisationDropdown
                            value={filtersObj?.selectedOrganisation || null}
                            onChange={(org) => { setFiltersObj({ ...filtersObj, selectedOrganisation: org || null, organisation_id: org?.organisation_id || '' }); getUserList({ ...filtersObj, selectedOrganisation: org || null, organisation_id: org?.organisation_id || '' }) }}
                            label={"Select Organisation"}
                        />
                    </Grid>
                }
            </Grid> */}
            <Grid container spacing={{ xs: 1, sm: 2 }} sx={{ alignItems: 'center', mb: { xs: 1, sm: 2 }, mt: { xs: 1, sm: 0 }, justifyContent: { xs: 'center', sm: 'space-between' } }} className='search-box-style'>
                <Grid item xs={12} sm={loggedInUser?.is_superuser ? 2 : 12}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <SearchBox
                            handleSearch={handleSearch}
                            value={filtersObj?.search || ''}
                        />
                    </Box>
                </Grid>
                {loggedInUser?.is_superuser && (
                    <Grid item xs={12} sm={3}>
                        <OrganisationDropdown
                            value={filtersObj?.selectedOrganisation || null}
                            onChange={(org) => {
                                setFiltersObj({
                                    ...filtersObj,
                                    selectedOrganisation: org || null,
                                    organisation_id: org?.organisation_id || ''
                                });
                                getUserList({
                                    ...filtersObj,
                                    selectedOrganisation: org || null,
                                    organisation_id: org?.organisation_id || ''
                                });
                            }}
                            label={"Select Organisation"}
                        />
                    </Grid>
                )}
            </Grid>

            <CommonTable
                columns={columns}
                data={userResponse?.list || []}
                muiTableContainerProps={{ sx: { maxHeight: 'calc(100vh - 347px)', width: '100%' } }}
                state={{
                    showColumnFilters: false,
                    columnVisibility,
                    isLoading
                }}
                // enableDensityToggle={false}
                // enableFullScreenToggle={false}
                // enableColumnActions={false}
                // enableColumnFilters={false}
                // enableHiding={false}
                // enableTopToolbar={false}
                // enablePagination={false}
                // manualPagination={false}
                renderBottomToolbar={() => (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', padding: 0, margin: 0 }}>
                        <Pagination
                            totalCount={userResponse?.count || 0}
                            rowsPerPage={filtersObj?.limit}
                            page={filtersObj?.page - 1}
                            handleChangePage={handleChangePage}
                            handleChangeRowsPerPage={handleChangeRowsPerPage}
                        />
                    </Box>
                )}
            />
            {isModalOpen && (
                <ChangePasswordModal
                    open={isModalOpen}
                    onClose={() => { setIsModalOpen(false) }}
                    onChangePassword={handleSubmit}
                />

            )}
            <ConfirmBox
                open={isConfirmOpen}
                onClose={handleDelete}
                options={{
                    title: 'Confirm Deletion',
                    text: 'Are you sure you want to delete this user?',
                    confirmButtonText: 'Delete',
                    cancelButtonText: 'Cancel',
                    confirmButtonColor: 'error',
                    cancelButtonColor: 'default',
                }}
            />
            <OrganisationModal
                open={modalOpen}
                onClose={handleCloseModal}
                organisation={selectedOrganisation}
            />

        </Box>
    );
};

export default List;
